import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import { responseHandler } from '../utils';
import { Envelope, HotelStripeFees, OutletKind } from './types';

/**
 * Prowizja HotelSpot od sprzedaży w punktach hotelu.
 *
 * WYŁĄCZNIE przez backend — hs-admin nie pisze tych wartości do Firestore. Endpointy
 * stoją za `verifyAdminAccess`, więc uprawnienie jest sprawdzane po stronie serwera,
 * a nie ukryciem przycisku w interfejsie. Stawka wynika z umowy z hotelem: gdyby dało
 * się ją zmienić z poziomu klienta, hotel wpisałby sobie zero i dalej sprzedawał.
 */

export const HOTEL_STRIPE_FEES_QUERY_KEY = 'hotel-stripe-fees';

export const useGetHotelStripeFees = (
  hotelId: string | undefined,
): UseQueryResult<HotelStripeFees, AxiosError> => useQuery<HotelStripeFees, AxiosError>({
  queryKey: [HOTEL_STRIPE_FEES_QUERY_KEY, hotelId],
  enabled: !!hotelId,
  retry: 0,
  queryFn: async () => {
    const response = await axiosInstance.get<Envelope<HotelStripeFees>>(
      '/v3-getHotelStripeFees',
      { params: { hotelId } },
    );
    return responseHandler(response).data;
  },
});

export interface SetFeeParams {
  hotelId: string;
  /** Bez `kind` → stawka domyślna hotelu. Z `kind` → stawka tego typu punktów. */
  kind?: OutletKind;
  /** `null` kasuje wpis typu i przywraca dziedziczenie (tylko razem z `kind`). */
  feePercent: number | null;
}

export const useSetHotelStripeFee = (): UseMutationResult<void, AxiosError, SetFeeParams> => useMutation<
  void,
  AxiosError,
  SetFeeParams
>({
  mutationFn: async (params) => {
    const response = await axiosInstance.post('/v3-setHotelStripeFee', params);
    return responseHandler(response);
  },
});

export interface SetAvailabilityParams {
  hotelId: string;
  available: boolean;
}

/**
 * Udostępnienie płatności online hotelowi. Zapis idzie przez backend za
 * `verifyAdminAccess` — flaga siedzi na dokumencie hotelu, do którego personel hotelu ma
 * prawo zapisu, więc jedynym miejscem, gdzie ta decyzja jest naprawdę nasza, jest serwer.
 */
export const useSetStripeAvailability = (): UseMutationResult<void, AxiosError, SetAvailabilityParams> => useMutation<
  void,
  AxiosError,
  SetAvailabilityParams
>({
  mutationFn: async (params) => {
    const response = await axiosInstance.post('/v3-setStripeAvailability', params);
    return responseHandler(response);
  },
});
